import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import apiClient from '../lib/api';

// Add responsive styles
const styles = `
  @media (max-width: 768px) {
    .hero-title {
      font-size: 2rem !important;
    }
    .hero-description {
      font-size: 16px !important;
    }
  }
`;

const Reset = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [token, setToken] = useState('');
  const [inviteData, setInviteData] = useState(null);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
      validateToken(tokenParam);
    } else {
      setValidating(false);
      setValidToken(false);
    }
  }, [searchParams]);

  const validateToken = async (tokenToValidate) => {
    try {
      setValidating(true);
      const response = await apiClient.validateInviteToken(tokenToValidate);
      
      if (response.success) {
        setValidToken(true);
        setInviteData(response.invite);
      } else {
        setValidToken(false);
        toast.error('Invalid or expired token');
      }
    } catch (error) {
      console.error('Error validating token:', error);
      setValidToken(false);
      toast.error('Invalid or expired token');
    } finally {
      setValidating(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await apiClient.resetPassword(token, formData.password);
      toast.success('Password set successfully! You can now log in.');
      navigate('/login');
    } catch (error) {
      console.error('Error setting password:', error);
      const errorMessage = error.response?.data?.error || 'Error setting password';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <>
        <style>{styles}</style>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          fontFamily: 'Inter, sans-serif',
          backgroundColor: '#FEFDF6',
        }}>
          <div>Validating token...</div>
        </div>
      </>
    );
  }

  if (!validToken) {
    return (
      <>
        <style>{styles}</style>
        <div style={{ 
          minHeight: '100vh',
          fontFamily: 'Inter, sans-serif',
          backgroundColor: '#FEFDF6',
          width: '100%',
          overflowX: 'hidden',
          boxSizing: 'border-box',
        }}>
          {/* Hero Section */}
          <div className="hero-section" style={{
            position: 'relative',
            minHeight: '100vh',
            paddingTop: '40px',
            paddingBottom: '40px',
            overflow: 'hidden',
            background: 'linear-gradient(to bottom right, #74C0E6, #FEFDF6, rgba(189, 182, 253, 0.3))',
            width: '100%',
            boxSizing: 'border-box',
          }}>
            {/* Decorative background elements */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              overflow: 'hidden',
              zIndex: 0,
            }}>
              <div style={{
                position: 'absolute',
                top: '80px',
                left: '40px',
                width: '256px',
                height: '256px',
                backgroundColor: 'rgba(32, 150, 243, 0.2)',
                borderRadius: '50%',
                filter: 'blur(60px)',
              }}></div>
              <div style={{
                position: 'absolute',
                bottom: '80px',
                right: '40px',
                width: '384px',
                height: '384px',
                backgroundColor: 'rgba(108, 204, 22, 0.2)',
                borderRadius: '50%',
                filter: 'blur(60px)',
              }}></div>
            </div>

            {/* Hero Content */}
            <div style={{
              maxWidth: '1200px',
              margin: '0 auto',
              padding: '0 16px',
              textAlign: 'center',
              position: 'relative',
              zIndex: 10,
            }}>
              {/* Logo */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '24px',
              }}>
                <div className="logo-group" style={{
                  position: 'relative',
                  backgroundColor: '#6CCC16',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  borderBottomLeftRadius: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  cursor: 'pointer',
                }}>
                  <div style={{
                    overflow: 'hidden',
                    transition: 'all 0.5s ease-in-out',
                    maxWidth: '46px',
                  }} className="logo-text-container">
                    <span style={{
                      fontWeight: 900,
                      fontSize: '24px',
                      letterSpacing: '-0.5px',
                      whiteSpace: 'nowrap',
                      display: 'block',
                    }}>IMObituary</span>
                  </div>
                  <div style={{
                    position: 'relative',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'visible',
                    width: '24px',
                    height: '24px',
                  }}>
                    {/* Mail truck */}
                    <div className="mail-truck" style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 10,
                      opacity: 0,
                    }}>
                      <i className="fa-solid fa-truck" style={{ fontSize: '12px', color: 'white' }}></i>
                    </div>
                    {/* Envelope icons */}
                    <i className="fa-regular fa-envelope envelope-closed-animate" style={{
                      fontSize: '18px',
                      color: 'white',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 20,
                      opacity: 1,
                    }}></i>
                    <i className="fa-regular fa-envelope-open envelope-open-animate" style={{
                      fontSize: '18px',
                      color: 'white',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 20,
                      opacity: 0,
                    }}></i>
                    {/* Dollar signs */}
                    <span className="dollar-sign-1" style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 30,
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      opacity: 0,
                    }}>$</span>
                    <span className="dollar-sign-2" style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 30,
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      opacity: 0,
                    }}>$</span>
                    <span className="dollar-sign-3" style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 30,
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      opacity: 0,
                    }}>$</span>
                  </div>
                  <div style={{
                    position: 'absolute',
                    bottom: '-8px',
                    left: 0,
                    width: 0,
                    height: 0,
                    borderTop: '8px solid #6CCC16',
                    borderRight: '8px solid transparent',
                  }}></div>
                </div>
              </div>

              <h1 className="hero-title" style={{
                fontSize: '3rem',
                fontWeight: 900,
                color: '#000000',
                marginBottom: '16px',
                letterSpacing: '-1px',
                textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
              }}>
                Reset Password
              </h1>
              <p className="hero-description" style={{
                fontSize: '20px',
                color: '#206884',
                maxWidth: '600px',
                margin: '0 auto 48px',
                fontWeight: 600,
              }}>
                This reset link is invalid or has expired. Please request a new one.
              </p>

              {/* Form Card */}
              <div style={{ 
                maxWidth: '500px',
                width: '100%',
                margin: '0 auto',
                boxSizing: 'border-box',
              }}>
                <div style={{ 
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  padding: '40px',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
                  width: '100%',
                  boxSizing: 'border-box',
                  textAlign: 'left',
                }}>
                  <div style={{ 
                    backgroundColor: '#f8d7da', 
                    color: '#721c24', 
                    padding: '12px', 
                    borderRadius: '8px', 
                    marginBottom: '20px',
                    border: '1px solid #f5c6cb'
                  }}>
                    This reset link is invalid or has expired. Please request a new one.
                  </div>

                  <button
                    onClick={() => navigate('/forgot')}
                    style={{
                      width: '100%',
                      backgroundColor: '#206884',
                      color: 'white',
                      border: 'none',
                      padding: '12px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      marginBottom: '20px',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#1a5568';
                      e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#206884';
                      e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                    }}
                  >
                    Go to Forgot Password
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div style={{ 
        minHeight: '100vh',
        fontFamily: 'Inter, sans-serif',
        backgroundColor: '#FEFDF6',
        width: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box',
      }}>
        {/* Hero Section */}
        <div className="hero-section" style={{
          position: 'relative',
          minHeight: '100vh',
          paddingTop: '40px',
          paddingBottom: '40px',
          overflow: 'hidden',
          background: 'linear-gradient(to bottom right, #74C0E6, #FEFDF6, rgba(189, 182, 253, 0.3))',
          width: '100%',
          boxSizing: 'border-box',
        }}>
          {/* Decorative background elements */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            zIndex: 0,
          }}>
            <div style={{
              position: 'absolute',
              top: '80px',
              left: '40px',
              width: '256px',
              height: '256px',
              backgroundColor: 'rgba(32, 150, 243, 0.2)',
              borderRadius: '50%',
              filter: 'blur(60px)',
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '80px',
              right: '40px',
              width: '384px',
              height: '384px',
              backgroundColor: 'rgba(108, 204, 22, 0.2)',
              borderRadius: '50%',
              filter: 'blur(60px)',
            }}></div>
          </div>

          {/* Hero Content */}
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 16px',
            textAlign: 'center',
            position: 'relative',
            zIndex: 10,
          }}>
            {/* Logo */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '24px',
            }}>
              <div className="logo-group" style={{
                position: 'relative',
                backgroundColor: '#6CCC16',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '8px',
                borderBottomLeftRadius: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                cursor: 'pointer',
              }}>
                <div style={{
                  overflow: 'hidden',
                  transition: 'all 0.5s ease-in-out',
                  maxWidth: '46px',
                }} className="logo-text-container">
                  <span style={{
                    fontWeight: 900,
                    fontSize: '24px',
                    letterSpacing: '-0.5px',
                    whiteSpace: 'nowrap',
                    display: 'block',
                  }}>IMObituary</span>
                </div>
                <div style={{
                  position: 'relative',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'visible',
                  width: '24px',
                  height: '24px',
                }}>
                  {/* Mail truck */}
                  <div className="mail-truck" style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 10,
                    opacity: 0,
                  }}>
                    <i className="fa-solid fa-truck" style={{ fontSize: '12px', color: 'white' }}></i>
                  </div>
                  {/* Envelope icons */}
                  <i className="fa-regular fa-envelope envelope-closed-animate" style={{
                    fontSize: '18px',
                    color: 'white',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 20,
                    opacity: 1,
                  }}></i>
                  <i className="fa-regular fa-envelope-open envelope-open-animate" style={{
                    fontSize: '18px',
                    color: 'white',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 20,
                    opacity: 0,
                  }}></i>
                  {/* Dollar signs */}
                  <span className="dollar-sign-1" style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 30,
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    opacity: 0,
                  }}>$</span>
                  <span className="dollar-sign-2" style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 30,
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    opacity: 0,
                  }}>$</span>
                  <span className="dollar-sign-3" style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 30,
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    opacity: 0,
                  }}>$</span>
                </div>
                <div style={{
                  position: 'absolute',
                  bottom: '-8px',
                  left: 0,
                  width: 0,
                  height: 0,
                  borderTop: '8px solid #6CCC16',
                  borderRight: '8px solid transparent',
                }}></div>
              </div>
            </div>

            <h1 className="hero-title" style={{
              fontSize: '3rem',
              fontWeight: 900,
              color: '#000000',
              marginBottom: '16px',
              letterSpacing: '-1px',
              textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
            }}>
              Set Password
            </h1>
            {inviteData && (
              <p className="hero-description" style={{
                fontSize: '20px',
                color: '#206884',
                maxWidth: '600px',
                margin: '0 auto 48px',
                fontWeight: 600,
              }}>
                Welcome, {inviteData.firstName}! Please create a password for your account.
              </p>
            )}

            {/* Form Card */}
            <div style={{ 
              maxWidth: '500px',
              width: '100%',
              margin: '0 auto',
              boxSizing: 'border-box',
            }}>
              <div style={{ 
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '40px',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
                width: '100%',
                boxSizing: 'border-box',
                textAlign: 'left',
              }}>
                <form onSubmit={handleSubmit}>
                  {/* Account Info */}
                  {inviteData && (
                    <div style={{ 
                      backgroundColor: '#d4edda', 
                      color: '#155724', 
                      padding: '12px', 
                      borderRadius: '8px', 
                      marginBottom: '20px',
                      border: '1px solid #c3e6cb'
                    }}>
                      <strong>Account:</strong> {inviteData.email}
                    </div>
                  )}

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ 
                      color: '#333', 
                      fontWeight: '500',
                      display: 'block',
                      marginBottom: '8px',
                      textAlign: 'left'
                    }}>
                      Password
                    </label>
                    <input 
                      type={showPassword ? 'text' : 'password'}
                      name="password" 
                      placeholder="Enter your password" 
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      disabled={loading}
                      style={{ 
                        border: '1px solid #ddd', 
                        padding: '10px 15px',
                        width: '100%',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }} 
                      required 
                    />
                    {errors.password && (
                      <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                        {errors.password}
                      </div>
                    )}
                    {!errors.password && (
                      <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
                        Must be at least 8 characters
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ 
                      color: '#333', 
                      fontWeight: '500',
                      display: 'block',
                      marginBottom: '8px',
                      textAlign: 'left'
                    }}>
                      Confirm Password
                    </label>
                    <input 
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword" 
                      placeholder="Confirm your password" 
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      disabled={loading}
                      style={{ 
                        border: '1px solid #ddd', 
                        padding: '10px 15px',
                        width: '100%',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }} 
                      required 
                    />
                    {errors.confirmPassword && (
                      <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                        {errors.confirmPassword}
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      width: '100%',
                      backgroundColor: '#206884',
                      color: 'white',
                      border: 'none',
                      padding: '12px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1,
                      marginBottom: '20px',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.target.style.backgroundColor = '#1a5568';
                        e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loading) {
                        e.target.style.backgroundColor = '#206884';
                        e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                      }
                    }}
                  >
                    {loading ? 'Setting Password...' : 'Set Password & Continue'}
                  </button>
                </form>

                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                  <p style={{ 
                    color: '#6b7280', 
                    fontSize: '14px', 
                    margin: 0 
                  }}>
                    Already have an account?{' '}
                    <button
                      onClick={() => navigate('/login')}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#206884',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.color = '#1a5568';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = '#206884';
                      }}
                    >
                      Sign in here
                    </button>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Reset;
